import React from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  IconArrowRightDashed,
  IconDeviceLaptop,
  IconMoon,
  IconSun,
} from "@tabler/icons-react";
import { useAtom } from "jotai";
import { appearanceAtom } from "@/stores/appearance-atom";
import { searchOpenAtom } from "@/stores/search-atom";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { type SidebarData } from "./layout/types";
import { ScrollArea } from "./ui/scroll-area";

interface CommandMenuProps {
  sidebarData: SidebarData;
}

export function CommandMenu({ sidebarData }: Readonly<CommandMenuProps>) {
  const navigate = useNavigate();
  const [open, setOpen] = useAtom(searchOpenAtom);
  const [, setAppearance] = useAtom(appearanceAtom);

  // TODO: Use a library to handle keyboard shortcuts
  // Listen for cmd/ctrl + k to open search
  React.useEffect(() => {
    const handleKeyDown = (e: Readonly<KeyboardEvent>) => {
      // Mac: cmd+k; Windows/Linux: ctrl+k
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(true);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [setOpen]);

  const runCommand = React.useCallback(
    (command: () => unknown) => {
      setOpen(false);
      command();
    },
    [setOpen],
  );

  return (
    <CommandDialog modal open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Type a command or search..." />
      <CommandList>
        <ScrollArea type="hover" className="h-72 pr-1">
          <CommandEmpty>No results found.</CommandEmpty>
          {sidebarData.navGroups.map((group) => (
            <CommandGroup key={group.title} heading={group.title}>
              {group.items.map((navItem, i) => {
                if (navItem.url) {
                  return (
                    <CommandItem
                      key={`${navItem.url}-${i}`}
                      value={navItem.title}
                      onSelect={() => {
                        runCommand(() => navigate({ to: navItem.url }));
                      }}
                    >
                      <div className="mr-2 flex h-4 w-4 items-center justify-center">
                        <IconArrowRightDashed className="text-muted-foreground/80 size-2" />
                      </div>
                      {navItem.title}
                    </CommandItem>
                  );
                }

                return navItem.items?.map((subItem, j) => (
                  <CommandItem
                    key={`${subItem.url}-${j}`}
                    value={subItem.title}
                    onSelect={() => {
                      runCommand(() => navigate({ to: subItem.url }));
                    }}
                  >
                    <div className="mr-2 flex h-4 w-4 items-center justify-center">
                      <IconArrowRightDashed className="text-muted-foreground/80 size-2" />
                    </div>
                    {subItem.title}
                  </CommandItem>
                ));
              })}
            </CommandGroup>
          ))}
          <CommandSeparator />
          <CommandGroup heading="Theme">
            <CommandItem
              onSelect={() =>
                runCommand(() =>
                  setAppearance((prev) => ({ ...prev, theme: "light" })),
                )
              }
            >
              <IconSun /> <span>Light</span>
            </CommandItem>
            <CommandItem
              onSelect={() =>
                runCommand(() =>
                  setAppearance((prev) => ({ ...prev, theme: "dark" })),
                )
              }
            >
              <IconMoon className="scale-90" />
              <span>Dark</span>
            </CommandItem>
            <CommandItem
              onSelect={() =>
                runCommand(() =>
                  setAppearance((prev) => ({ ...prev, theme: "system" })),
                )
              }
            >
              <IconDeviceLaptop />
              <span>System</span>
            </CommandItem>
          </CommandGroup>
        </ScrollArea>
      </CommandList>
    </CommandDialog>
  );
}
